import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  PERMISSIONS,
  REPORT_TYPES,
  type BillingReportSummary,
  type CustomerReportSummary,
  type MandateReportSummary,
  type NotificationReportSummary,
  type ReportType,
} from '@solar/contracts';

import {
  AdminIcon,
  ManagementHeader,
  adminManagementStyles as styles,
} from '../../../../components/admin/AdminManagement';
import { Alert } from '../../../../components/Alert';
import { useAuth } from '../../../../features/auth/auth-context';
import { reportingApi } from '../../../../features/reporting/reporting.api';
import { toDisplayMessage } from '../../../../lib/error-message';
import { formatNgn } from '../../../../lib/format-currency';
import { formatStatusLabel } from '../../../../lib/format-status';

const DESCRIPTIONS: Record<ReportType, string> = {
  customers: 'Registrations, onboarding and application stages.',
  applications: 'Application lifecycle records for the chosen period.',
  contracts: 'Accepted contract activity and current state.',
  mandates: 'Mandate creation, activation and exception counts.',
  billing: 'Due, collected and overdue portfolio summary.',
  payments: 'Posted payment transactions for reconciliation.',
  overdue: 'Overdue schedule details for collections follow-up.',
  notifications: 'Delivery status and notification backlog.',
  audit: 'Administrative audit events for accountability.',
};
function isCustomer(value: unknown): value is CustomerReportSummary {
  return typeof value === 'object' && value !== null && 'registrations' in value;
}
function isBilling(value: unknown): value is BillingReportSummary {
  return typeof value === 'object' && value !== null && 'dueCount' in value;
}
function isMandate(value: unknown): value is MandateReportSummary {
  return typeof value === 'object' && value !== null && 'pendingActivation' in value;
}
function isNotification(value: unknown): value is NotificationReportSummary {
  return typeof value === 'object' && value !== null && 'queued' in value;
}

function Summary({ value }: { value: unknown }): React.JSX.Element {
  if (isCustomer(value))
    return (
      <dl className={styles.summaryGrid}>
        <div>
          <dt>Registrations</dt>
          <dd>{value.registrations}</dd>
        </div>
        <div>
          <dt>Incomplete onboarding</dt>
          <dd>{value.incompleteOnboarding}</dd>
        </div>
        {Object.entries(value.applicationsByStatus).map(([status, count]) => (
          <div key={status}>
            <dt>{formatStatusLabel(status)}</dt>
            <dd>{count}</dd>
          </div>
        ))}
      </dl>
    );
  if (isBilling(value))
    return (
      <dl className={styles.summaryGrid}>
        <div>
          <dt>Due</dt>
          <dd>
            {value.dueCount} · {formatNgn(value.dueAmount)}
          </dd>
        </div>
        <div>
          <dt>Collected</dt>
          <dd>
            {value.collectedCount} · {formatNgn(value.collectedAmount)}
          </dd>
        </div>
        <div>
          <dt>Collection rate</dt>
          <dd>{value.collectionRate}</dd>
        </div>
        <div>
          <dt>Overdue</dt>
          <dd>
            {value.overdueCount} · {formatNgn(value.overdueAmount)}
          </dd>
        </div>
      </dl>
    );
  if (isMandate(value))
    return (
      <dl className={styles.summaryGrid}>
        <div>
          <dt>Created</dt>
          <dd>{value.created}</dd>
        </div>
        <div>
          <dt>Pending activation</dt>
          <dd>{value.pendingActivation}</dd>
        </div>
        <div>
          <dt>Active</dt>
          <dd>{value.active}</dd>
        </div>
        <div>
          <dt>Failed</dt>
          <dd>{value.failed}</dd>
        </div>
      </dl>
    );
  if (isNotification(value))
    return (
      <dl className={styles.summaryGrid}>
        <div>
          <dt>Queued</dt>
          <dd>{value.queued}</dd>
        </div>
        <div>
          <dt>Sent</dt>
          <dd>{value.sent}</dd>
        </div>
        <div>
          <dt>Failed</dt>
          <dd>{value.failed}</dd>
        </div>
        <div>
          <dt>Stale processing</dt>
          <dd>{value.staleProcessing}</dd>
        </div>
      </dl>
    );
  return (
    <p className={styles.empty}>
      This report is available for CSV export. Its detailed rows are not summarized in the browser.
    </p>
  );
}

export function ReportsPage(): React.JSX.Element {
  const { can } = useAuth();
  const [selected, setSelected] = useState<ReportType>('customers');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [range, setRange] = useState<{ from?: string; to?: string }>({});
  const report = useQuery({
    queryKey: ['admin', 'reports', selected, range],
    queryFn: () => reportingApi.getReport(selected, range),
  });
  const exportReport = useMutation({
    mutationFn: () => reportingApi.exportReport(selected, range),
  });
  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Portfolio intelligence"
        title="Reports"
        subtitle="Run and export the platform’s authoritative operational reports."
        icon="report"
        context="Report centre"
      />
      <section className={styles.hero}>
        <div>
          <h2>Choose the report that answers your operational question</h2>
          <p>The catalogue below contains exactly the report types supported by the repository.</p>
        </div>
        <strong>{REPORT_TYPES.length} report types</strong>
      </section>
      <section className={styles.cardsGrid} aria-label="Report catalogue">
        {REPORT_TYPES.map((type) => (
          <article className={styles.reportCard} key={type}>
            <span className={styles.reportIcon}>
              <AdminIcon
                name={
                  type === 'billing' || type === 'payments' || type === 'overdue'
                    ? 'money'
                    : type === 'customers'
                      ? 'customer'
                      : 'report'
                }
              />
            </span>
            <h2>{formatStatusLabel(type)}</h2>
            <p>{DESCRIPTIONS[type]}</p>
            <button
              className={`${styles.buttonLink} ${selected === type ? '' : styles.buttonSecondary}`}
              type="button"
              onClick={() => setSelected(type)}
            >
              {selected === type ? 'Selected' : 'Open report'}
            </button>
          </article>
        ))}
      </section>
      <form
        className={styles.filterPanel}
        onSubmit={(event) => {
          event.preventDefault();
          setRange({ from: from || undefined, to: to || undefined });
        }}
      >
        <label>
          From
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </label>
        <button type="submit">Run {formatStatusLabel(selected)} report</button>
        {can(PERMISSIONS.REPORTS_EXPORT) && (
          <button
            className={styles.buttonSecondary}
            type="button"
            onClick={() => exportReport.mutate()}
            disabled={exportReport.isPending}
          >
            <AdminIcon name="download" /> Export CSV
          </button>
        )}
      </form>
      <section className={styles.mainCard}>
        <header className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <AdminIcon name="report" />
            </span>
            <div>
              <h2>{formatStatusLabel(selected)} report</h2>
              <p>{DESCRIPTIONS[selected]}</p>
            </div>
          </div>
        </header>
        <div className={styles.summaryBody}>
          {report.isPending && <p className={styles.empty}>Loading report…</p>}
          {report.isError && <Alert tone="error">{toDisplayMessage(report.error)}</Alert>}
          {exportReport.isError && (
            <Alert tone="error">{toDisplayMessage(exportReport.error)}</Alert>
          )}
          {report.data && <Summary value={report.data} />}
        </div>
      </section>
    </div>
  );
}
